import React, { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import { MessageSquare, Send, Plus, Trash2, Link as LinkIcon, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import type { Feedback, Drill, DrillDrawingPoint } from '../types';
import DrillDrawingCanvas from './DrillDrawingCanvas';
import { useRipple } from '../lib/useRipple';

interface FeedbackFormProps {
  gameId: string;
  onFeedbackSubmitted?: () => void;
}

export default function FeedbackForm({
  gameId,
  onFeedbackSubmitted,
}: FeedbackFormProps) {
  const user = useStore((state) => state.user);
  const [authorName, setAuthorName] = useState('');
  const [content, setContent] = useState('');
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDrillForm, setShowDrillForm] = useState(false);
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  const [expandedDrills, setExpandedDrills] = useState<Record<string, boolean>>(
    {}
  );
  const [currentDrill, setCurrentDrill] = useState<Drill>({
    title: '',
    description: '',
    videoUrl: '',
    exerciseDrawing: [],
  });
  const { createRipple } = useRipple();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const feedbackData: Omit<Feedback, 'id'> = {
        gameId,
        authorName,
        authorId: user.uid,
        content,
        drills: drills.map((drill) => ({
          ...drill,
          exerciseDrawing: drill.exerciseDrawing || [],
        })),
        createdAt: new Date(),
      };

      await addDoc(collection(db, 'feedback'), feedbackData);
      setAuthorName('');
      setContent('');
      setDrills([]);
      onFeedbackSubmitted?.();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleDrawingChange = (points: DrillDrawingPoint[]) => {
    setCurrentDrill((prev) => ({
      ...prev,
      exerciseDrawing: points,
    }));
  };

  const handleAddDrill = () => {
    if (!currentDrill.title || !currentDrill.description) return;

    setDrills((prev) => [
      ...prev,
      {
        ...currentDrill,
        exerciseDrawing: currentDrill.exerciseDrawing || [],
      },
    ]);

    setCurrentDrill({
      title: '',
      description: '',
      videoUrl: '',
      exerciseDrawing: [],
    });
    setShowDrillForm(false);
    setShowDrawingCanvas(false);
  };

  const handleRemoveDrill = (index: number) => {
    setDrills(drills.filter((_, i) => i !== index));
  };

  const toggleDrillDrawing = (index: number) => {
    const key = `drill-${index}`;
    setExpandedDrills((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className='glass-card p-6'>
      <h3 className='text-lg font-medium mb-4 flex items-center gap-2'>
        <MessageSquare className='h-5 w-5' />
        Provide Feedback
      </h3>

      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label
            htmlFor='authorName'
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            Your Name
          </label>
          <input
            type='text'
            id='authorName'
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
            required
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor='content'
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            Feedback
          </label>
          <textarea
            id='content'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
            rows={4}
            required
            disabled={loading}
            placeholder={t('feedbackForm.feedbackPlaceholder')}
          />
        </div>

        <div>
          <div className='flex justify-between items-center mb-2'>
            <label className='block text-sm font-medium text-gray-700'>
              {t('feedbackForm.suggestedDrills')}
            </label>
            <button
              type='button'
              onClick={() => setShowDrillForm(true)}
              className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1'
            >
              <Plus className='h-4 w-4' />
              {t('feedbackForm.addDrill')}
            </button>
          </div>

          {drills.length > 0 && (
            <div className='space-y-2 mb-4'>
              {drills.map((drill, index) => (
                <div key={index} className='bg-gray-50 rounded-lg p-3 relative'>
                  <button
                    type='button'
                    onClick={() => handleRemoveDrill(index)}
                    className='absolute top-2 right-2 text-gray-400 hover:text-red-500'
                  >
                    <Trash2 className='h-4 w-4' />
                  </button>
                  <h4 className='font-medium text-gray-900'>{drill.title}</h4>
                  <p className='text-gray-600 text-sm mt-1'>
                    {drill.description}
                  </p>
                  <div className='flex flex-wrap gap-2 mt-2'>
                    {drill.videoUrl && (
                      <a
                        href={drill.videoUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1'
                      >
                        <LinkIcon className='h-4 w-4' />
                        Watch Video
                      </a>
                    )}
                    {drill.exerciseDrawing &&
                      drill.exerciseDrawing.length > 0 && (
                        <div>
                          <button
                            type='button'
                            onClick={() => toggleDrillDrawing(index)}
                            className='px-3 py-1.5 border border-[#1b3968] text-[#1b3968] hover:bg-[#1b3968] hover:text-white rounded-md text-sm flex items-center gap-2 transition-colors'
                          >
                            <Pencil className='h-4 w-4' />
                            Exercise Drawing
                            {expandedDrills[`drill-${index}`] ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            )}
                          </button>
                          {expandedDrills[`drill-${index}`] && (
                            <div className='mt-3'>
                              <DrillDrawingCanvas
                                points={drill.exerciseDrawing}
                                readOnly={true}
                              />
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showDrillForm && (
            <div className='bg-gray-50 rounded-lg p-4 space-y-3 mb-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('feedbackForm.drillTitle')}
                </label>
                <input
                  type='text'
                  value={currentDrill.title}
                  onChange={(e) =>
                    setCurrentDrill({ ...currentDrill, title: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  placeholder={t('feedbackForm.drillTitlePlaceholder')}
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('feedbackForm.description')}
                </label>
                <textarea
                  value={currentDrill.description}
                  onChange={(e) =>
                    setCurrentDrill({
                      ...currentDrill,
                      description: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  rows={3}
                  placeholder={t('feedbackForm.descriptionPlaceholder')}
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('feedbackForm.videoUrlOptional')}
                </label>
                <input
                  type='url'
                  value={currentDrill.videoUrl}
                  onChange={(e) =>
                    setCurrentDrill({
                      ...currentDrill,
                      videoUrl: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  placeholder={t('feedbackForm.videoUrlPlaceholder')}
                />
              </div>
              <div>
                <button
                  type='button'
                  onClick={() => setShowDrawingCanvas(!showDrawingCanvas)}
                  className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1'
                >
                  <Pencil className='h-4 w-4' />
                  {showDrawingCanvas ? 'Hide Drawing Canvas' : 'Draw Exercise'}
                </button>
                {showDrawingCanvas && (
                  <div className='mt-4'>
                    <DrillDrawingCanvas
                      points={currentDrill.exerciseDrawing}
                      onChange={handleDrawingChange}
                    />
                    <p className='mt-2 text-sm text-gray-500'>
                      Draw the movement pattern for this drill on the rink
                      diagram
                    </p>
                  </div>
                )}
              </div>
              <div className='flex justify-end space-x-3 pt-2'>
                <button
                  type='button'
                  onClick={() => {
                    setShowDrillForm(false);
                    setShowDrawingCanvas(false);
                  }}
                  className='px-4 py-2 text-gray-700 hover:text-gray-900'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleAddDrill}
                  className='px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2'
                  disabled={!currentDrill.title || !currentDrill.description}
                >
                  <Plus className='h-4 w-4' />
                  {t('feedbackForm.addDrill')}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          type='submit'
          disabled={loading}
          className='btn-mui-primary btn-ripple w-full px-4 py-2'
          onClick={createRipple}
        >
          {loading ? (
            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
          ) : (
            <>
              <Send className='h-5 w-5' />
              Submit Feedback
            </>
          )}
        </button>
      </form>
    </div>
  );
}